import { useEffect, useState } from "react";
import api from "../utils/api";
import { useUserStore } from "../store/userStore";

/**
 * Roles de plataforma que administran al equipo fuera de CC-CONSOLE.
 * Espejo de `ADMIN_ROLES` en el backend (`middlewares/staffAdmin.js`).
 */
export const STAFF_ADMIN_ROLES = ["admin", "admin reseller"];

export interface StaffAccess {
  /** ¿Administra al equipo (ve todo)? */
  isStaffAdmin: boolean;
  /** ¿Administra Nómina? Quien administra al equipo, más el rol `finance` (#1243). */
  isPayrollAdmin?: boolean;
  /** ¿Rige la regla de dueños (CC-CONSOLE, #1007): quien no administra ve solo lo suyo? */
  ownerPolicy: boolean;
  /**
   * ¿Respondió el servidor? (#1145). Mientras es `false` los dos campos de
   * arriba valen `false` por precaución. Si nunca responde, se queda en `false`:
   * la app Equipo sigue «comprobando» (falla cerrada).
   */
  resolved?: boolean;
  /**
   * ¿Ya hay decisión: respondió el servidor o se agotaron los reintentos (#1142)?
   * Quien vaya a REDIRIGIR por no administrar (pestañas de Nómina) debe esperar
   * a `loaded` para no sacar a un dueño de su pantalla al recargar.
   */
  loaded?: boolean;
}

const NONE: StaffAccess = { isStaffAdmin: false, ownerPolicy: false, resolved: false };

/**
 * Qué ve en Gestión de Staff la persona que usa la consola (#742, #1007).
 *
 * **Esto es para PINTAR, no para autorizar.** Quien decide es el servidor en
 * cada endpoint; aquí solo se evita ofrecer botones que van a responder 403.
 *
 * La respuesta sale del servidor (`GET /staffAccess`), no del rol guardado en
 * el navegador: en CC-CONSOLE administran dos PERSONAS (Isaías y Daniel), no un
 * rol, y el navegador no puede saber eso. Fuera de CC-CONSOLE el servidor aplica
 * el rol (admin, admin reseller u owner #1024 por su id). Mientras llega (o si
 * falla) se asume que NO administra: el lado seguro es enseñar de menos.
 */
const cache = new Map<string, StaffAccess>();
const inflight = new Map<string, Promise<StaffAccess | null>>();

/** `null` = no se pudo preguntar (red, 5xx): no se guarda y se reintenta. */
function loadStaffAccess(key: string): Promise<StaffAccess | null> {
  const pending = inflight.get(key);
  if (pending) return pending;
  const p = api
    .get("/staffAccess")
    .then((res: { data?: { data?: { data?: Partial<StaffAccess> } & Partial<StaffAccess> } }) => {
      // `response()` del backend envuelve en `{ meta, data: { message, data } }`:
      // el acceso viene en `data.data`. Leer un nivel arriba daba siempre `false`
      // y dejaba a los dueños en solo lectura en todo Staff (#1036).
      const outer = res?.data?.data || {};
      const data = outer.data || outer;
      const isStaffAdmin = data.isStaffAdmin === true;
      const access = {
        isStaffAdmin,
        // Un servidor anterior a #1243 no manda el campo: Nómina sigue a `isStaffAdmin`.
        isPayrollAdmin: isStaffAdmin || data.isPayrollAdmin === true,
        ownerPolicy: data.ownerPolicy === true,
        resolved: true,
      };
      cache.set(key, access);
      return access;
    })
    .catch(() => null)
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

const RETRY_MS = [2000, 8000];

/**
 * @param enabled `false` = no preguntar (p. ej. el tenant no tiene Gestión de
 *   Staff): devuelve el valor por omisión, sin resolver.
 */
export type StaffAccessScope = "payroll";

/**
 * @param scope `"payroll"` = la respuesta es para Nómina: `isStaffAdmin` incluye
 *   también al rol `finance` (#1243). Sin ámbito, es Gestión de Staff en general.
 */
export function useStaffAccess(enabled = true, scope?: StaffAccessScope): StaffAccess {
  const user = useUserStore((state) => state.user);
  // La respuesta es de ESTA persona: se guarda por usuario para que un cambio
  // de sesión no herede la de la anterior.
  const key = enabled ? String(user?.id ?? user?.email ?? "") : "";
  const [access, setAccess] = useState<StaffAccess>(() => {
    const cached = key ? cache.get(key) : undefined;
    return cached ? { ...cached, loaded: true } : { ...NONE, loaded: !key };
  });

  useEffect(() => {
    if (!key) {
      setAccess({ ...NONE, loaded: true });
      return;
    }
    const cached = cache.get(key);
    if (cached) {
      setAccess({ ...cached, loaded: true });
      return;
    }
    setAccess({ ...NONE, loaded: false });
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const attempt = (n: number) => {
      loadStaffAccess(key).then((got) => {
        if (!alive) return;
        if (got) setAccess({ ...got, loaded: true });
        // Un fallo pasajero no debe dejar a un dueño en solo lectura hasta recargar.
        else if (n < RETRY_MS.length) timer = setTimeout(() => attempt(n + 1), RETRY_MS[n]);
        // Sin respuesta tras los reintentos: se queda del lado seguro (no administra).
        else setAccess({ ...NONE, loaded: true });
      });
    };
    attempt(0);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [key]);

  if (scope === "payroll") return { ...access, isStaffAdmin: access.isStaffAdmin || access.isPayrollAdmin === true };
  return access;
}

/** Atajo: ¿administra al equipo? Con `"payroll"`, ¿administra Nómina? */
export function useIsStaffAdmin(scope?: StaffAccessScope): boolean {
  return useStaffAccess(true, scope).isStaffAdmin;
}

export default useIsStaffAdmin;
